import { useState, type ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom-v6'
import { toast } from 'sonner'
import { ApiError } from '@clouway/api'
import { Card, CardContent, InputGroup, InputGroupAddon, InputGroupInput, Skeleton } from '@clouway/ui'
import { Search } from 'lucide-react'

import { useSearchCustomers, type Customer } from '@/features/customers'
import { useDebounce } from '@/hooks/useDebounce'
import { MoveTargetCard } from '../components/MoveTargetCard'
import { useMoveTrackingObject, useTrackingObject } from '../hooks'

export function MoveTrackingObject() {
  const { t } = useTranslation('feature')
  const { id = '', tid = '' } = useParams<{ id: string; tid: string }>()
  const navigate = useNavigate()
  const [query, setQuery] = useState('')
  const debouncedQuery = useDebounce(query, 500)
  const { data: customers, isFetching, isSuccess } = useSearchCustomers(debouncedQuery)
  const { data: object } = useTrackingObject(id, tid)
  const { mutateAsync: move } = useMoveTrackingObject(id, tid)

  const handleMove = async (customer: Customer) => {
    try {
      await move(customer.id)
      toast.success(t('trackingObjects.move.successMessageTitle'), {
        description: t('trackingObjects.move.successMessageDescription', {
          lpn: object?.lpn ?? '',
          customerName: customer.name,
        }),
      })
      navigate(`/customers/${customer.id}/tracking-objects/${tid}/status`)
    } catch (error) {
      toast.error(t('trackingObjects.move.errorMessage'), {
        description: t('trackingObjects.move.errorDescription', {
          status: error instanceof ApiError ? error.status : '',
        }),
      })
    }
  }

  return (
    <div className="mx-auto flex w-full max-w-2xl flex-col gap-3">
      <Card>
        <CardContent className="pt-6">
          <InputGroup>
            <InputGroupAddon>
              <Search />
            </InputGroupAddon>
            <InputGroupInput
              type="search"
              value={query}
              placeholder={t('trackingObjects.move.search')}
              aria-label={t('trackingObjects.move.search')}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
            />
          </InputGroup>
        </CardContent>
      </Card>

      {isFetching && <Skeleton className="h-20 w-full" />}

      {isSuccess &&
        !isFetching &&
        customers.map((customer) =>
          // The current owner can't be a move target.
          customer.id === id ? (
            <Card key={customer.id} className="border-primary">
              <CardContent className="pt-6">
                <span className="text-primary text-sm font-semibold">{customer.name}</span>
              </CardContent>
            </Card>
          ) : (
            <MoveTargetCard key={customer.id} customer={customer} lpn={object?.lpn ?? ''} onMove={handleMove} />
          ),
        )}
    </div>
  )
}
